import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { blogPosts, blogCategories } from '../data/blogData';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const BlogList = () => {
  const { t, language, isRTL } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredPosts = activeCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category.en.toLowerCase() === activeCategory);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-AE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-[#060B14]" data-testid="blog-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Outfit', sans-serif" }}
          >
            {t('blog.title')}
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto">
            {t('blog.subtitle')}
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="px-6 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className={`flex flex-wrap gap-2 justify-center ${isRTL ? 'flex-row-reverse' : ''}`}>
            {blogCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === category.id
                    ? 'bg-[#25D366] text-white'
                    : 'bg-white/10 text-slate-400 hover:bg-white/20 hover:text-white'
                }`}
                data-testid={`blog-category-${category.id}`}
              >
                {category.name[language]}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="px-6 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article 
                key={post.id}
                className="bg-[#0A0F1C] border border-white/10 rounded-xl overflow-hidden hover:border-[#25D366]/50 transition-all group"
                data-testid={`blog-post-card-${post.id}`}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title[language]}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-[#25D366] text-white text-xs font-medium rounded-full">
                      {post.category[language]}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className={`p-6 ${isRTL ? 'text-right' : ''}`}>
                  {/* Meta */}
                  <div className={`flex items-center gap-4 text-slate-500 text-sm mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Calendar size={14} />
                      {formatDate(post.date)}
                    </span>
                    <span className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Clock size={14} />
                      {post.readTime[language]}
                    </span>
                  </div>

                  {/* Title */}
                  <h2 
                    className="text-xl font-semibold text-white mb-3 line-clamp-2 group-hover:text-[#25D366] transition-colors"
                    style={{ fontFamily: isRTL ? "'Noto Sans Arabic', sans-serif" : "'Outfit', sans-serif" }}
                  >
                    {post.title[language]}
                  </h2>

                  {/* Excerpt */}
                  <p className="text-slate-400 text-sm mb-4 line-clamp-3">
                    {post.excerpt[language]}
                  </p>

                  {/* Read More */}
                  <Link 
                    to={`/blog/${post.slug}`}
                    className={`inline-flex items-center gap-2 text-[#25D366] text-sm font-medium hover:gap-3 transition-all ${isRTL ? 'flex-row-reverse' : ''}`}
                    data-testid={`read-more-${post.id}`}
                  >
                    {t('blog.readMore')}
                    <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-400">No posts found in this category.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogList;
